import Logger from './logger';

function normalizeTicker(ticker) {
  if (ticker === null || ticker === undefined) {
    return null;
  }

  const normalizedTicker = `${ticker}`.trim();

  return normalizedTicker || null;
}

/**
 * Builds a TradeStation Agent (TA) SSO URL for mobile trading actions.
 * Used for Buy, Sell, Detail, and Margin buttons on Symbol Detail Page and Marketboard.
 *
 * @param {string} subdomain - The mxp subdomain (e.g., 'mxp1', 'mxp2', 'mxp3')
 * @param {string} taDispId - TA display ID (102 for Symbol Detail, 106 for Position)
 * @param {string} ticker - Stock symbol (optional, not included for Sell/Position screens)
 * @param {string} qsValue - Quick-select value (optional, 'buy' for Buy button)
 * @returns {string|null} The complete SSO URL or null if validation fails
 */
export function getTaSsoUrl(subdomain, taDispId, ticker = null, qsValue = null) {
  if (!subdomain) {
    Logger.error('getTaSsoUrl: subdomain is required');
    return null;
  }

  if (!taDispId) {
    Logger.error('getTaSsoUrl: taDispId is required');
    return null;
  }

  const normalizedTicker = normalizeTicker(ticker);

  // Mobile SSO continues to use the TradeAgent endpoint, including the /pc/servlet path.
  let url = `https://${subdomain}.monex.co.jp/pc/servlet/ITS/foreign/TransitionSsoForTradeAgent`;
  url += `?taDispId=${taDispId}`;
  url += `&prevDispId=0101`;

  if (normalizedTicker) {
    url += `&ticker=${encodeURIComponent(normalizedTicker)}`;
  }

  if (qsValue) {
    // qsValue appends to the existing query string instead of starting a new one.
    url += `?qsValue=${qsValue}`;
  }

  return url;
}

/**
 * Gets the TA SSO URL for a Buy action on mobile.
 * Includes ticker and qsValue=buy parameters.
 *
 * @param {string} subdomain - The mxp subdomain
 * @param {string} ticker - Stock symbol
 * @returns {string|null} The Buy action SSO URL
 */
export function getTaBuyUrl(subdomain, ticker) {
  const normalizedTicker = normalizeTicker(ticker);

  if (!normalizedTicker) {
    Logger.error('getTaBuyUrl: ticker is required');
    return null;
  }

  return getTaSsoUrl(subdomain, '102', normalizedTicker, 'buy');
}

/**
 * Gets the TA SSO URL for a Sell action on mobile.
 * Does NOT include ticker parameter (Position Screen is not symbol-specific).
 *
 * @param {string} subdomain - The mxp subdomain
 * @returns {string|null} The Sell action SSO URL
 */
export function getTaSellUrl(subdomain) {
  return getTaSsoUrl(subdomain, '106');
}

/**
 * Gets the TA SSO URL for a Detail/Symbol Detail action on mobile.
 * Includes ticker parameter but no qsValue.
 *
 * @param {string} subdomain - The mxp subdomain
 * @param {string} ticker - Stock symbol
 * @returns {string|null} The Detail action SSO URL
 */
export function getTaDetailUrl(subdomain, ticker) {
  const normalizedTicker = normalizeTicker(ticker);

  if (!normalizedTicker) {
    Logger.error('getTaDetailUrl: ticker is required');
    return null;
  }

  return getTaSsoUrl(subdomain, '102', normalizedTicker);
}
